import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Bell, Check, ExternalLink, Trash2, X, CheckCheck, AlertTriangle, Info, CheckCircle } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { useAuthStore } from '@/core/stores/useAuthStore';
import {
  fetchNotifications,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  type TenantNotification,
  type NotificationCategory,
} from '@/infrastructure/api/notifications';
import { Notification } from '@/generated/shared-constants';
import { Spinner } from '@/shared/ui';
import { timeAgo } from '@/utils/format';
import { errorMessage, tEnum } from '@/infrastructure/i18n';

interface NotificationPanelProps {
  position?: 'left' | 'right';
  onClose: () => void;
  onNotificationRead: () => void;
}

const LEVEL_STYLES = {
  critical: { bg: 'bg-red-100', icon: 'text-red-700', dot: 'bg-red-700' },
  error: { bg: 'bg-red-50', icon: 'text-red-500', dot: 'bg-red-500' },
  warning: { bg: 'bg-amber-50', icon: 'text-amber-500', dot: 'bg-amber-500' },
  success: { bg: 'bg-green-50', icon: 'text-green-500', dot: 'bg-green-500' },
  info: { bg: 'bg-blue-50', icon: 'text-blue-500', dot: 'bg-blue-500' },
} as const;

const LevelIcon: React.FC<{ level: TenantNotification['level']; className?: string }> = ({ level, className }) => {
  const style = LEVEL_STYLES[level];
  const cls = `w-4 h-4 ${style.icon} ${className ?? ''}`;
  if (level === 'critical' || level === 'error' || level === 'warning') return <AlertTriangle className={cls} />;
  if (level === 'success') return <CheckCircle className={cls} />;
  return <Info className={cls} />;
};

function notificationCategoryLabel(category: NotificationCategory): string {
  return tEnum('notificationCategory', category);
}

/**
 * Translate notification title/message that come from cloud as i18n keys.
 * Format: "notification.aeat_rejected.title" → t('notification.aeat_rejected.title')
 * Format: "notification.aeat_rejected.message:param1:param2" → t('notification.aeat_rejected.message', {0: param1, 1: param2})
 * Format: {"key":"notification.foo.message","params":{"days":3}} → t(key, params)
 * Format: params 的**值**带 `Notification.I18N_PARAM_PREFIX` 前缀时先翻译再插值
 *         ({"entity_type":"$t:anomalies.entity_operator"} → 「操作员」)
 */
export function translateNotificationField(raw: string, t: (k: string, params?: Record<string, string | number>) => string): string {
  const structured = parseStructuredNotificationField(raw);
  if (structured) {
    const translated = t(structured.key, structured.params && resolveI18nParams(structured.params, t));
    return translated === structured.key ? raw : translated;
  }

  if (!raw.startsWith('notification.')) return raw;
  const parts = raw.split(':');
  const key = parts[0];
  const translated = t(key);
  // If t() returned the key itself, it means no translation found — return raw
  if (translated === key) return raw;
  if (parts.length <= 1) return translated;
  // Replace {0}, {1}, ... with params
  let result = translated;
  for (let i = 1; i < parts.length; i++) {
    result = result.replace(`{${i - 1}}`, parts[i]);
  }
  return result;
}

/**
 * params 的值本身可以是一个 i18n key —— cloud 用 `I18N_PARAM_PREFIX` 标记。
 *
 * 为什么需要这一层:通知的语言只有 console 知道(界面语言是登录用户的偏好),
 * 而 `entity_type` 这类闭合枚举在 cloud 侧只有 `as_str()` 的英文 token。不翻译的话
 * 中文模板会渲染成「检测到 1 条高风险 operator 异常」。
 *
 * 前缀常量走 `@/generated/shared-constants`(源在 `shared/src/consts/notification.rs`),
 * 不在这里再写一遍字面量:两端各写一份的话,改了一侧的结果不是报错而是老板读到
 * `$t:anomalies.entity_operator` 这串东西。
 */
function resolveI18nParams(
  params: Record<string, string | number>,
  t: (k: string, params?: Record<string, string | number>) => string,
): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(params).map(([key, value]) => [
      key,
      typeof value === 'string' && value.startsWith(Notification.I18N_PARAM_PREFIX)
        ? t(value.slice(Notification.I18N_PARAM_PREFIX.length))
        : value,
    ]),
  );
}

function parseStructuredNotificationField(raw: string): { key: string; params?: Record<string, string | number> } | null {
  if (!raw.startsWith('{')) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || typeof parsed.key !== 'string') return null;
  const params = normalizeNotificationParams(parsed.params);
  return Object.keys(params).length === 0 ? { key: parsed.key } : { key: parsed.key, params };
}

function normalizeNotificationParams(value: unknown): Record<string, string | number> {
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    Object.entries(value)
      .flatMap(([key, param]) => {
        if (typeof param === 'string' || typeof param === 'number') {
          return [[key, param]];
        }
        if (typeof param === 'boolean') {
          return [[key, String(param)]];
        }
        return [];
      }),
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  onClose,
  onNotificationRead,
}) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<TenantNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<'all' | 'unread'>('all');
  const token = useAuthStore(s => s.token);

  const loadNotifications = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      setError('');
      const res = await fetchNotifications(token, { limit: 20 });
      setNotifications(res.data);
    } catch (err) {
      setError(errorMessage(t, err));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleMarkAsRead = useCallback(async (id: string) => {
    if (!token) return;
    try {
      await markAsRead(token, id);
      setError('');
      setNotifications(prev => prev.map(n => (n.id === id ? { ...n, is_read: true } : n)));
      onNotificationRead();
    } catch (err) {
      setError(errorMessage(t, err));
    }
  }, [token, onNotificationRead, t]);

  const handleMarkAllRead = useCallback(async () => {
    if (!token) return;
    try {
      await markAllAsRead(token);
      setError('');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      onNotificationRead();
    } catch (err) {
      setError(errorMessage(t, err));
    }
  }, [token, onNotificationRead, t]);

  const handleDelete = useCallback(async (id: string) => {
    if (!token) return;
    try {
      await deleteNotification(token, id);
      setError('');
      setNotifications(prev => prev.filter(n => n.id !== id));
      onNotificationRead();
    } catch (err) {
      setError(errorMessage(t, err));
    }
  }, [token, onNotificationRead, t]);

  const filtered = tab === 'unread' ? notifications.filter(n => !n.is_read) : notifications;
  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]" onClick={onClose} />

      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)] sm:w-[28rem] md:w-[32rem] bg-white rounded-2xl shadow-2xl border border-slate-200/80 z-50 overflow-hidden max-h-[80vh] flex flex-col animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 bg-primary-50 rounded-xl flex items-center justify-center">
                <Bell className="w-5 h-5 text-primary-500" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">{t('notification.title')}</h3>
                {unreadCount > 0 && (
                  <p className="text-xs text-slate-500">{unreadCount} {t('notification.unread')}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium px-2 py-1.5 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  {t('notification.mark_all_read')}
                </button>
              )}
              <button
                onClick={onClose}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-slate-100 p-0.5 rounded-lg">
            <button
              onClick={() => setTab('all')}
              className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-all cursor-pointer ${
                tab === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {t('notification.tab_all')}
            </button>
            <button
              onClick={() => setTab('unread')}
              className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-all cursor-pointer flex items-center justify-center gap-1 ${
                tab === 'unread' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {t('notification.tab_unread')}
              {unreadCount > 0 && (
                <span className="min-w-[18px] h-[18px] flex items-center justify-center bg-primary-500 text-white text-xs font-bold rounded-full px-1">
                  {unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* List */}
        <div className="overflow-y-auto flex-1">
          {error ? (
            <div className="m-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
              {error}
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner className="w-6 h-6 text-primary-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-6">
              <div className="w-14 h-14 bg-slate-50 rounded-full flex items-center justify-center mb-3">
                <Bell className="w-7 h-7 text-slate-300" />
              </div>
              <p className="text-sm font-medium text-slate-500">{t('notification.empty')}</p>
              <p className="text-xs text-slate-400 mt-1">{t('notification.empty_hint')}</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map(n => {
                const style = LEVEL_STYLES[n.level];
                return (
                  <div
                    key={n.id}
                    className={`group px-5 py-3.5 transition-colors ${
                      !n.is_read ? 'bg-primary-50/30' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${style.bg}`}>
                        <LevelIcon level={n.level} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              {!n.is_read && <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${style.dot}`} />}
                              <span className="font-semibold text-sm text-slate-900 truncate">{translateNotificationField(n.title, t)}</span>
                            </div>
                            <p className="text-xs text-slate-600 mt-1 leading-relaxed">{translateNotificationField(n.message, t)}</p>
                          </div>
                          {/* Actions — always visible on touch, hover-reveal on desktop (≥md) */}
                          <div className="flex items-center gap-0.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity shrink-0">
                            {!n.is_read && (
                              <button
                                onClick={() => handleMarkAsRead(n.id)}
                                className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
                                title={t('notification.mark_read')}
                              >
                                <Check className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {n.action_url && (
                              <button
                                onClick={() => {
                                  const url = n.store_id
                                    ? `/stores/${n.store_id}${n.action_url}`
                                    : n.action_url!;
                                  navigate(url);
                                  if (!n.is_read) handleMarkAsRead(n.id);
                                  onClose();
                                }}
                                className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(n.id)}
                              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                              title={t('notification.delete')}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-xs text-slate-400">{timeAgo(n.created_at)}</span>
                          <span className="text-xs text-slate-300">·</span>
                          <span className="text-xs text-slate-400 capitalize">{notificationCategoryLabel(n.category)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
